
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Eye, Download, Trash2 } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useIsMobile } from '@/hooks/use-mobile';

type CertificatesTableProps = {
  searchTerm?: string;
}

export function CertificatesTable({ searchTerm = '' }: CertificatesTableProps) {
  const isMobile = useIsMobile();
  const { data: certificates, isLoading, error } = useQuery({
    queryKey: ['certificates', searchTerm],
    queryFn: async () => {
      try {
        // Use the get_certificate_details function for a comprehensive join
        const { data, error } = await supabase.rpc('get_certificate_details');
        
        if (error) throw error;
        
        // Filter based on search term if provided
        if (searchTerm) {
          return data.filter((cert: any) => 
            cert.unique_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            cert.user_full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (cert.certificate_number && cert.certificate_number.toLowerCase().includes(searchTerm.toLowerCase()))
          );
        }
        
        return data;
      } catch (err) {
        console.error("Error fetching certificates:", err);
        return [];
      }
    },
  });
  
  if (isLoading) {
    return <div>Loading certificates...</div>;
  }
  
  if (error) {
    return <div>Error loading certificates</div>;
  }
  
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Certificate #</TableHead>
          <TableHead>User</TableHead>
          {!isMobile && <TableHead>Course</TableHead>}
          {!isMobile && <TableHead>Issued Date</TableHead>}
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {certificates && certificates.length > 0 ? (
          certificates.map((certificate: any) => (
            <TableRow key={certificate.id}>
              <TableCell className="font-medium">{certificate.certificate_number || certificate.unique_code}</TableCell>
              <TableCell>{certificate.user_full_name}</TableCell>
              {!isMobile && <TableCell>{certificate.course_name}</TableCell>}
              {!isMobile && <TableCell>{new Date(certificate.issued_at).toLocaleDateString()}</TableCell>}
              <TableCell>
                <span className={`px-2 py-1 rounded-full text-xs ${certificate.is_revoked ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                  {certificate.is_revoked ? 'Revoked' : 'Active'}
                </span>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" size="icon" title="View Details">
                    <Eye className="h-4 w-4" />
                  </Button>
                  {!isMobile && (
                    <>
                      <Button variant="ghost" size="icon" title="Download Certificate">
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={isMobile ? 4 : 6} className="text-center py-4">No certificates found</TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
